import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import EFilterButton from '@/components/everridge/EFilterButton';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { FilterHelper } from '@/utils/filterHelper';
import { cn } from '@/lib/utils';
import IntegrationMessagesTable from './IntegrationMessagesTable';
import IntegrationMessagesPageFilter from './IntegrationMessagesPageFilter';
import IntegrationMessagesPurgeDialog from './IntegrationMessagesPurgeDialog';
import IntegrationMessagesDeleteAllDialog from './IntegrationMessagesDeleteAllDialog';
import { useIntegrationMessagesColumns } from './useIntegrationMessagesColumns';
import {
    useIntegrationMessageMutations,
    useIntegrationMessagesListApi,
} from './useIntegrationMessagesApi';
import { useIntegrationMessagesFilter } from './hooks/useIntegrationMessagesFilter';
import type { IntegrationMessageKind } from './integration-message.types';
import { getIntegrationMessageBasePath } from './integration-message.utils';

type Props = {
    kind: IntegrationMessageKind;
};

const IntegrationMessagesListPage = ({ kind }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const columns = useIntegrationMessagesColumns(kind);

    const { filter, setFilter } = useIntegrationMessagesFilter(kind);
    const [page, setPage] = useState(() => Math.floor(filter.skip / filter.take));
    const [searchText, setSearchText] = useState(filter.searchText);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [showFilter, setShowFilter] = useState(false);
    const { list, isLoading } = useIntegrationMessagesListApi(kind, {
        suspendFetching: showFilter,
    });
    const { bulkDeleteMutation, purgeProcessedMutation, purgeAllMutation } = useIntegrationMessageMutations(kind);
    const [showPurgeDialog, setShowPurgeDialog] = useState(false);
    const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);

    useEffect(() => {
        const totalCount = list?.totalCount ?? 0;
        if ((totalCount === 0 && filter.skip > 0) || (totalCount > 0 && filter.skip >= totalCount)) {
            setFilter({ skip: 0 });
            setPage(0);
        }
    }, [list?.totalCount, filter.skip, setFilter]);

    useEffect(() => {
        setPage(Math.floor(filter.skip / filter.take));
    }, [filter.skip, filter.take]);

    if (userRole !== Roles.superAdmin) return null;

    const activeFiltersCount = FilterHelper.countFilterValues(filter, ['take', 'skip', 'searchText']);

    const titleKey =
        kind === 'inbox'
            ? 'integrationMessages.inbox.title'
            : kind === 'outbox'
              ? 'integrationMessages.outbox.title'
              : kind === 'emailOutbox'
                ? 'integrationMessages.emailOutbox.title'
              : 'integrationMessages.internalOutbox.title';

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({ skip: filter.take * newPage });
    };

    const handleSearchApply = () => {
        setPage(0);
        setFilter({ searchText, skip: 0 });
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) return;
        await bulkDeleteMutation.mutateAsync(selectedIds);
        EToast({
            variant: 'success',
            message: t('integrationMessages.toast.deletedSelected'),
        });
        setSelectedIds([]);
    };

    const handlePurgeProcessed = async (olderThanDays: number) => {
        const result = await purgeProcessedMutation.mutateAsync(olderThanDays);
        EToast({
            variant: 'success',
            message: t('integrationMessages.toast.purged', { count: result.deletedCount }),
        });
    };

    const handleDeleteAll = async () => {
        const result = await purgeAllMutation.mutateAsync();
        EToast({
            variant: 'success',
            message: t('integrationMessages.toast.deletedAll', { count: result.deletedCount }),
        });
        setSelectedIds([]);
    };

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader
                title={t(titleKey)}
                search={
                    <ESearchInput
                        value={searchText}
                        setValue={setSearchText}
                        onSearch={handleSearchApply}
                        placeholder={t('integrationMessages.filters.searchPlaceholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
            >
                <EFilterButton
                    name={t('elements.buttons.filter')}
                    filtersQuantity={activeFiltersCount}
                    onClick={() => setShowFilter(true)}
                    isMobile={isMobile}
                />
            </EPageHeader>

            <div className="flex flex-wrap gap-2">
                <Button
                    variant="destructive"
                    disabled={selectedIds.length === 0 || bulkDeleteMutation.isPending}
                    onClick={() => void handleDeleteSelected()}
                    className={cn(isMobile && 'flex-1')}
                >
                    {t('integrationMessages.actions.deleteSelected', {
                        count: selectedIds.length,
                    })}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setShowPurgeDialog(true)}
                    className={cn(isMobile && 'flex-1')}
                >
                    {t('integrationMessages.actions.purgeProcessed')}
                </Button>
                <Button
                    variant="destructive"
                    onClick={() => setShowDeleteAllDialog(true)}
                    className={cn(isMobile && 'flex-1')}
                >
                    {t('integrationMessages.actions.deleteAll')}
                </Button>
            </div>

            <IntegrationMessagesTable
                columns={columns}
                data={list}
                page={page}
                onPageChange={handlePageChange}
                paginationStep={filter.take}
                isLoadingPage={isLoading}
                onSelectedIdsChange={setSelectedIds}
                onRowClick={(row) => navigate(`${getIntegrationMessageBasePath(kind)}/${row.id}`)}
            />

            {showFilter && (
                <IntegrationMessagesPageFilter
                    kind={kind}
                    onClose={() => setShowFilter(false)}
                    isMobile={isMobile}
                />
            )}

            <IntegrationMessagesPurgeDialog
                open={showPurgeDialog}
                onOpenChange={setShowPurgeDialog}
                onPurgeProcessed={handlePurgeProcessed}
                isPurging={purgeProcessedMutation.isPending}
            />

            <IntegrationMessagesDeleteAllDialog
                open={showDeleteAllDialog}
                onOpenChange={setShowDeleteAllDialog}
                onDeleteAll={handleDeleteAll}
                isDeleting={purgeAllMutation.isPending}
            />
        </section>
    );
};

export default IntegrationMessagesListPage;
